import { useQuery } from '@tanstack/react-query';

import { api } from './client';

/** Matches AdminOverview in apps/api/src/modules/superadmin/services/admin-overview.service.ts. */
export interface AdminOverview {
  generatedAt: string;
  users: {
    total: number;
    signupsToday: number;
    signups7d: number;
    signups30d: number;
    active7d: number;
    active30d: number;
    unverified: number;
    banned: number;
  };
  signupsDaily: Array<{ date: string; signups: number }>;
  workspace: { teams: number; sharedTeams: number; projects: number; tasksCreated: number; storageBytes: number };
  billing: { pendingUpgradeRequests: number; activeSubscriptions: number };
  errors: { open24h: number; open7d: number };
  email: { sent24h: number; sent7d: number; sent30d: number; failed7d: number; queued: number };
}

export interface AdminBadges {
  pendingUpgradeRequests: number;
}

export interface UserActivity {
  lastSeenAt: string | null;
  activeSessions: number;
  lastActionAt: string | null;
  actions30d: number;
}

export interface TeamUsage {
  projects: number;
  activeProjects: number;
  tasksCreated: number;
  lastActionAt: string | null;
  actions30d: number;
  pendingUpgradeRequestId: string | null;
}

export function useAdminOverview() {
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<AdminOverview>('/api/admin/overview'),
    refetchInterval: 120_000,
  });
}

/** Sidebar counters — polled every minute while the console is open. */
export function useAdminBadges() {
  return useQuery({
    queryKey: ['admin', 'badges'],
    queryFn: () => api<AdminBadges>('/api/admin/overview/badges'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useUserActivity(id: string) {
  return useQuery({
    queryKey: ['admin', 'user', id, 'activity'],
    queryFn: () => api<UserActivity>(`/api/admin/users/${id}/activity`),
  });
}

export function useTeamUsage(id: string) {
  return useQuery({
    queryKey: ['admin', 'team', id, 'usage'],
    queryFn: () => api<TeamUsage>(`/api/admin/teams/${id}/usage`),
  });
}
